import * as Sentry from "@sentry/vue";

import config from "@/app/config.js";

const shouldFilterError = (event, hint) => {
  const error = hint?.originalException;
  if (!error) return false;

  const message = error.message || "";

  // Filter network errors (user connectivity issues)
  if (/network\s*error/i.test(message)) return true;

  // Filter common 4xx errors that don't provide actionable debugging info:
  // 400 - Bad Request (validation errors, handled by UI)
  // 401 - Unauthorized (auth flow, handled by app)
  // 403 - Forbidden (permission-based access, not a bug)
  // 404 - Not Found (user error, old bookmarks, deleted content)
  // 408 - Request Timeout (network issue)
  // 413 - Payload Too Large (user uploaded file too big)
  // 422 - Unprocessable Entity (form validation, expected)
  // 429 - Too Many Requests (rate limiting, expected)
  const status = error.response?.status || error.context?.response?.status;
  const ignoredStatusCodes = [400, 401, 403, 404, 408, 413, 422, 429];
  if (ignoredStatusCodes.includes(status)) return true;

  // Filter Vue DOM errors caused by browser extensions (e.g., password managers)
  if (/nextSibling/.test(message)) return true;

  // Filter third-party script errors (e.g. Zalo SDK injected by browser/extensions)
  if (/zaloJSV2/.test(message)) return true;

  // Filter video autoplay policy errors (browser-enforced, not actionable)
  if (error.name === "NotAllowedError") return true;

  // Filter request aborted errors (user navigated away during pending request)
  if (/Request aborted/.test(message)) return true;

  return false;
};

const initSentry = (app, router) => {
  Sentry.init({
    app,
    dsn: config.VITE_APP_SENTRY_DSN,
    integrations: [Sentry.browserTracingIntegration({ router }), Sentry.replayIntegration()],
    environment: config.VITE_APP_SENTRY_ENVIRONMENT,
    tracesSampleRate: 0, //  Don't capture performance data
    tracePropagationTargets: [
      "localhost",
      /sensity-testing-.*\.surge\.sh/, // Match domains like x-testing-my-pr-name.surge.sh
      /^https:\/\/platform\.sensity\.ai/,
      /^https:\/\/platform\.staging\.sensity\.ai/,
    ],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    beforeSend(event, hint) {
      if (shouldFilterError(event, hint)) return null;
      return event;
    },

    ignoreErrors: [
      /No error message/,
      // Chunk loading errors
      /Importing a module script failed/,
      /Loading chunk \d+ failed/,
      /Failed to fetch dynamically imported module/,
      /Loading CSS chunk/,
      /ChunkLoadError/,
      /Unable to preload CSS for/,
      /Failed to load resource/,
      /Load failed/,
      // Vite-specific patterns
      /assets\/.*\.js/,
      // Sentry OpenFeature integration errors (no provider configured)
      /feature named .* was not found/,
    ],
  });
};

export default initSentry;
